import type { PatientPlanRecord } from '../../../../../../types/patientPlan.types';
import type { AdjudicatorPlan } from '../../../../../../services/adjudicatorsService';
import type { Coverage } from './vacationRules';

/**
 * PRX-130 — the default coverage for the vacation supply calculator, from the
 * patient's plans on file. Staff can always override it; the default always
 * says where it came from.
 *
 * - Fillware plans carry a BIN, so they are matched to the PRX-128 adjudicator
 *   directory: BIN 610054 is ODB, a `discountPlan` row is a discount card,
 *   any other directory row is a private plan.
 * - Kroll plans carry no BIN. Kroll's own provincial sub-plan codes are read
 *   instead (`ODB`, `ODBNH`, `TRIL`; reconciliation-logic.ts, verified live
 *   2026-07-20). Any other Kroll plan can't be told apart from a discount
 *   card, so it is unknown.
 * - Trillium is not in the directory: a plan coded `TRIL` or named Trillium is TDP.
 *
 * Precedence: TDP, then ODB, then private, then cash (a discount card or a
 * cash plan). An unknown plan blocks a private or cash default — it might be
 * the plan that decides — so staff pick instead.
 */
export type PlanDirectoryAnswer =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; plans: AdjudicatorPlan[] };

export type PlanKind = 'tdp' | 'odb' | 'private' | 'discount' | 'cash' | 'unknown' | 'pending';

export interface PlanClass {
  kind: PlanKind;
  reason: string;
}

export interface PlanForCoverage {
  plan: PatientPlanRecord;
  /** Null when the plan was not looked up (no usable BIN, or not Fillware). */
  directory: PlanDirectoryAnswer | null;
}

export type PlansRead = { status: 'loading' } | { status: 'error' } | { status: 'ready'; plans: PlanForCoverage[] };

export interface CoverageDefault {
  coverage: Coverage | null;
  /** Where the default came from, or why there is none. */
  source: string;
  pending: boolean;
}

const ODB_BIN = '610054';

/**
 * PRX-128 directory rows that are public or federal programs, keyed by the
 * directory id (backend-v2/src/adjudicators/adjudicator-directory.data.ts).
 * Their vacation supply rules are not private-plan rules, and the rule table
 * has none for them, so they never default to Private: staff choose.
 */
export const PUBLIC_PROGRAM_DIRECTORY_IDS: ReadonlySet<string> = new Set([
  '610068-15-non-insured-health-benefits-nihb-program',
  '610047-co-interim-federal-health-ifh',
  '610047-vo-veterans-affairs-canada-canadian-forces-rcmp',
  '600526-12-pshcp-public-service-health-care-plan',
  '600526-49-wsib-workplace-safety-insurance-board',
  '600526-37-wsbc-bc-worksafe-bc',
  '610068-31-minist-re-de-l-emploi-et-de-la-solidarit-sociale-mess',
]);
const KROLL_ODB_CODES = new Set(['ODB', 'ODBNH']);
const TRILLIUM_CODE = 'TRIL';

const nameOf = (plan: PatientPlanRecord): string =>
  [plan.planName, plan.subPlanName].map((s) => (s ?? '').trim()).filter(Boolean).join(' / ') || 'a plan with no name';

const codesOf = (plan: PatientPlanRecord): string[] =>
  [plan.planCode, plan.subPlanCode].map((s) => String(s ?? '').trim().toUpperCase()).filter(Boolean);

export function classifyPlan(
  plan: PatientPlanRecord,
  directory: PlanDirectoryAnswer | null,
  databaseType: string | null,
): PlanClass {
  const name = nameOf(plan);
  const codes = codesOf(plan);
  if (codes.includes(TRILLIUM_CODE) || /trillium/i.test(name)) {
    return { kind: 'tdp', reason: `Trillium plan on file: ${name}` };
  }
  if (codes.includes('CASH') || /^\s*cash\s*$/i.test(plan.planName ?? '')) {
    return { kind: 'cash', reason: `Cash plan on file: ${name}` };
  }
  if (databaseType === 'fillware') {
    const bin = plan.adjHostId == null ? '' : String(plan.adjHostId).trim();
    if (!bin) return { kind: 'unknown', reason: `No BIN on file for ${name}` };
    if (!directory) return { kind: 'unknown', reason: `The BIN or carrier on file for ${name} is not in a form the adjudicator directory uses` };
    if (directory.status === 'loading') return { kind: 'pending', reason: 'Reading the adjudicator directory' };
    if (directory.status === 'error') return { kind: 'unknown', reason: `Couldn't read the adjudicator directory for ${name}` };
    if (directory.plans.length === 0) return { kind: 'unknown', reason: `BIN ${bin} (${name}) is not in the adjudicator directory` };
    const discount = directory.plans.find((row) => row.discountPlan);
    if (discount) return { kind: 'discount', reason: `Discount card on file: ${discount.name}` };
    const publicProgram = directory.plans.find((row) => PUBLIC_PROGRAM_DIRECTORY_IDS.has(row.id));
    if (publicProgram) {
      return { kind: 'unknown', reason: `${publicProgram.name} is a public program; choose the coverage` };
    }
    const odb = directory.plans.find((row) => row.bin === ODB_BIN);
    if (odb) return { kind: 'odb', reason: `ODB plan on file: ${odb.name} (BIN ${ODB_BIN})` };
    return { kind: 'private', reason: `Private plan on file: ${directory.plans[0].name}` };
  }
  if (databaseType) {
    const odbCode = codes.find((code) => KROLL_ODB_CODES.has(code));
    if (odbCode) return { kind: 'odb', reason: `ODB plan on file: ${name} (provincial plan code ${odbCode})` };
    return {
      kind: 'unknown',
      reason: `The pharmacy system gives no BIN, so ${name} can't be told apart from a discount card`,
    };
  }
  return { kind: 'unknown', reason: 'The pharmacy system is not known yet' };
}

export function defaultCoverage(read: PlansRead, databaseType: string | null): CoverageDefault {
  if (read.status === 'loading') return { coverage: null, source: "Reading the patient's plans", pending: true };
  if (read.status === 'error') {
    return { coverage: null, source: "Couldn't read the patient's plans. Choose the coverage.", pending: false };
  }
  const active = read.plans.filter((p) => !p.plan.inactive);
  if (active.length === 0) return { coverage: 'cash', source: 'No active plan on file', pending: false };

  const classes = active.map((p) => classifyPlan(p.plan, p.directory, databaseType));
  const first = (kind: PlanKind) => classes.find((c) => c.kind === kind);

  if (classes.some((c) => c.kind === 'pending')) {
    return { coverage: null, source: 'Reading the adjudicator directory', pending: true };
  }
  const tdp = first('tdp');
  if (tdp) return { coverage: 'tdp', source: tdp.reason, pending: false };
  const odb = first('odb');
  if (odb) return { coverage: 'odb', source: odb.reason, pending: false };

  const unknown = classes.filter((c) => c.kind === 'unknown');
  if (unknown.length > 0) {
    const reasons = unknown.map((c) => c.reason).join('. ');
    const source = /choose the coverage$/i.test(reasons) ? `${reasons}.` : `${reasons}. Choose the coverage.`;
    return { coverage: null, source, pending: false };
  }
  const privatePlan = first('private');
  if (privatePlan) return { coverage: 'private', source: privatePlan.reason, pending: false };
  const discount = first('discount');
  if (discount) return { coverage: 'cash', source: `Only a discount card on file, so cash: ${discount.reason.replace(/^Discount card on file: /, '')}`, pending: false };
  const cash = first('cash') as PlanClass;
  return { coverage: 'cash', source: cash.reason, pending: false };
}
